import React, { useEffect, useMemo, useState } from 'react';
import { PageContainer } from '../components/Container/PageContainer';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { alpha } from '@mui/material/styles';
import {
  getThreadsPeople,
  upsertThreadsPerson,
  setThreadsPersonActive,
} from '../utils/dataFetch';
import { dbService } from '../firebaseInstance';
import { getPublicAvatarUrl } from '../utils/publicAvatarUtils';
import { subscribeCategories } from '../services/adminCategoryManager';

const isValidHttpsUrl = (value) => {
  if (!value) return true;
  try {
    const u = new URL(value);
    return u.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

const MAX_BIO = 80;

const AdminThreadsPeople = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  // Monthly participants auto list
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyCandidates, setMonthlyCandidates] = useState([]);
  const [qIssue, setQIssue] = useState('');
  const [onlyUnregistered, setOnlyUnregistered] = useState(false);

  // Form state
  const [userId, setUserId] = useState('');
  const [bio, setBio] = useState('');
  const [businessLink, setBusinessLink] = useState('');
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [profileImage, setProfileImage] = useState('');
  // Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState([]);

  const canSave = useMemo(() => {
    if (!userId) return false;
    if (bio && bio.length > MAX_BIO) return false;
    if (!isValidHttpsUrl(businessLink)) return false;
    return true;
  }, [userId, bio, businessLink]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getThreadsPeople({ limit: 200, activeOnly, q });
      setList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly]);

  // Subscribe categories for selection (active only at render time)
  useEffect(() => {
    const unsub = subscribeCategories(
      (list) => setCategories(Array.isArray(list) ? list : []),
      () => setCategories([])
    );
    return () => unsub && unsub();
  }, []);

  const loadMonthlyParticipants = async () => {
    setMonthlyLoading(true);
    try {
      // 1) gather user IDs from all issues' userIdList
      const snap = await dbService
        .collection('magazines')
        .doc('monthlyThreads')
        .collection('issues')
        .get();
      const idSet = new Set();
      snap.docs.forEach((d) => {
        const data = d.data() || {};
        const arr = Array.isArray(data.userIdList) ? data.userIdList : [];
        arr.forEach((v) => v != null && idSet.add(String(v)));
      });
      const ids = Array.from(idSet);
      // 2) fetch user snapshots from user/{id}
      const batchSize = 50;
      const results = [];
      for (let i = 0; i < ids.length; i += batchSize) {
        const slice = ids.slice(i, i + batchSize);
        // eslint-disable-next-line no-await-in-loop
        const snaps = await Promise.all(
          slice.map((id) => dbService.collection('user').doc(id).get())
        );
        snaps.forEach((s, idx) => {
          const id = slice[idx];
          const data = s.exists ? (s.data() || {}) : {};
          const prof = data.profile || {};
          results.push({
            id,
            userId: id,
            username: prof.username || data.username || '',
            name: prof.name || data.name || '',
            profileImage: getPublicAvatarUrl(String(id), 160),
          });
        });
      }
      // 3) dedupe by id and sort by username
      const map = new Map();
      results.forEach((r) => map.set(r.id, r));
      const unique = Array.from(map.values()).sort((a, b) => {
        const au = (a.username || '').toLowerCase();
        const bu = (b.username || '').toLowerCase();
        if (au < bu) return -1;
        if (au > bu) return 1;
        return 0;
      });
      setMonthlyCandidates(unique);
    } finally {
      setMonthlyLoading(false);
    }
  };

  useEffect(() => {
    loadMonthlyParticipants();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSave) return;
    setSaving(true);
    try {
      const snapshot = {
        username: username || undefined,
        name: name || undefined,
        profileImage: profileImage || (userId ? getPublicAvatarUrl(String(userId), 320) : undefined),
      };
      const labelMap = new Map(categories.map((c) => [c.key, (c.labels && (c.labels.ko || c.labels.en)) || c.key]));
      const categoryLabels = selectedCategoryKeys.map((k) => labelMap.get(k) || String(k));
      await upsertThreadsPerson(userId, { 
        bio: bio || '', 
        businessLink: businessLink || null, 
        snapshot,
        categoryKeys: selectedCategoryKeys,
        categoryLabels,
      });
      setUserId('');
      setBio('');
      setBusinessLink('');
      setUsername('');
      setName('');
      setProfileImage('');
      setSelectedCategoryKeys([]);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, next) => {
    await setThreadsPersonActive(id, next);
    await load();
  };

  const quickFillFromUser = async () => {
    // Helper: fetch snapshot from user/{userId} if exists
    if (!userId) return;
    const snap = await dbService.collection('user').doc(String(userId)).get();
    if (snap.exists) {
      const data = snap.data();
      const prof = data?.profile || {};
      setUsername((prev) => prev || prof.username || '');
      setName((prev) => prev || prof.name || '');
      setProfileImage((prev) => prev || getPublicAvatarUrl(String(userId), 320) || '');
    }
  };

  return (
    <PageContainer expand>
      <Stack spacing={4} sx={{ width: '100%' }}>
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={600}>
            Threads 인물 사전 관리
          </Typography>
          <Typography variant="body1" color="text.secondary">
            bio(한줄 소개)와 businessLink를 입력해 참가자를 등록/수정합니다.
          </Typography>
        </Stack>

        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    label="User ID"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value.trim())}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    label="Username (snapshot)"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    label="Name (snapshot)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    label="Profile Image URL (snapshot)"
                    value={profileImage}
                    onChange={(e) => setProfileImage(e.target.value)}
                    placeholder="https://..."
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    label={`Bio (한줄 소개, 최대 ${MAX_BIO}자)`}
                    value={bio}
                    inputProps={{ maxLength: MAX_BIO }}
                    onChange={(e) => setBio(e.target.value)}
                    fullWidth
                    helperText={`${bio.length}/${MAX_BIO}`}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Business Link (https)"
                    value={businessLink}
                    onChange={(e) => setBusinessLink(e.target.value)}
                    error={!!businessLink && !isValidHttpsUrl(businessLink)}
                    helperText={businessLink && !isValidHttpsUrl(businessLink) ? 'https 링크만 허용됩니다' : '선택'}
                    placeholder="https://..."
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="threads-people-categories-label">카테고리</InputLabel>
                    <Select
                      labelId="threads-people-categories-label"
                      multiple
                      value={selectedCategoryKeys}
                      onChange={(e) => setSelectedCategoryKeys(e.target.value)}
                      label="카테고리"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const cat = categories.find((c) => c.key === value);
                            const label = (cat?.labels && (cat.labels.ko || cat.labels.en)) || value;
                            return <Chip key={value} label={label} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {categories
                        .filter((c) => c.isActive !== false)
                        .map((c) => {
                          const label = (c.labels && (c.labels.ko || c.labels.en)) || c.key;
                          return (
                            <MenuItem key={c.key} value={c.key}>
                              {label}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Stack direction="row" spacing={1}>
                    <Button onClick={quickFillFromUser} variant="outlined">유저 스냅샷 채우기</Button>
                    <Box sx={{ flex: 1 }} />
                    <Button type="submit" variant="contained" disabled={!canSave || saving}>
                      {saving ? '저장 중...' : '저장'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h6" fontWeight={600}>월간 참가자 자동 목록</Typography>
                <Box sx={{ flex: 1 }} />
                <TextField
                  label="검색 (username/name)"
                  value={qIssue}
                  onChange={(e) => setQIssue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadMonthlyParticipants()}
                  size="small"
                />
                <FormControlLabel
                  control={<Switch checked={onlyUnregistered} onChange={(e) => setOnlyUnregistered(e.target.checked)} />}
                  label="미등록만"
                />
                <Button onClick={loadMonthlyParticipants} variant="outlined" disabled={monthlyLoading}>
                  {monthlyLoading ? '로딩...' : '새로고침'}
                </Button>
              </Stack>
              <Grid container spacing={2}>
                {monthlyCandidates
                  .filter((c) => {
                    const needle = (qIssue || '').trim().toLowerCase();
                    if (!needle) return true;
                    const u = (c.username || '').toLowerCase();
                    const n = (c.name || '').toLowerCase();
                    return u.includes(needle) || n.includes(needle) || String(c.id).includes(needle);
                  })
                  .filter((c) => {
                    if (!onlyUnregistered) return true;
                    const exists = list.some((p) => String(p.id) === String(c.id));
                    return !exists;
                  })
                  .map((c) => {
                    const exists = list.some((p) => String(p.id) === String(c.id));
                    return (
                      <Grid key={c.id} size={{ xs: 12, md: 6, lg: 4 }}>
                        <Box sx={{
                          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                          borderRadius: 2,
                          p: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            @{c.username || c.name || c.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">{c.name || '-'}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                            <Typography variant="caption" color={exists ? 'primary.main' : 'text.secondary'}>
                              {exists ? '등록됨' : '미등록'}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Button size="small" variant="outlined" onClick={() => {
                              setUserId(String(c.id));
                              setUsername(c.username || '');
                              setName(c.name || '');
                              setProfileImage(c.profileImage || '');
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}>폼에 채우기</Button>
                          </Stack>
                        </Box>
                      </Grid>
                    );
                  })}
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="검색 (username/name)"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && load()}
                  size="small"
                />
                <FormControlLabel
                  control={<Switch checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />}
                  label="활성만"
                />
                <Button onClick={load} variant="outlined" disabled={loading}>
                  {loading ? '로딩...' : '새로고침'}
                </Button>
              </Stack>
              <Grid container spacing={2}>
                {list.map((p) => (
                  <Grid key={p.id} size={{ xs: 12, md: 6, lg: 4 }}>
                    <Box sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      borderRadius: 2,
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        @{p.username || p.name || p.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.bio || '-'}
                      </Typography>
                      {p.businessLink && (
                        <Typography variant="body2">
                          <a href={p.businessLink} target="_blank" rel="noreferrer">비지니스 링크</a>
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <FormControlLabel
                          control={<Switch checked={!!p.isActive} onChange={(e) => toggleActive(p.id, e.target.checked)} />}
                          label={p.isActive ? '활성' : '비활성'}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" variant="outlined" onClick={() => {
                          setUserId(String(p.userId || p.id));
                          setUsername(p.username || '');
                          setName(p.name || '');
                          setProfileImage(p.profileImage || '');
                          setBio(p.bio || '');
                          setBusinessLink(p.businessLink || '');
                          setSelectedCategoryKeys(Array.isArray(p.categoryKeys) ? p.categoryKeys : []);
                        }}>편집</Button>
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </PageContainer>
  );
};

export default AdminThreadsPeople;


